import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { AvatarSize, FallbackAvatar, ImgEasyResult } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { useCacheContext } from '@/context/CacheContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useDateFmt } from '@/hooks/useDateFmt'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { UploadAvatar } from '@/components/UploadAvatar'

function WelcomeAvatar() {
  const { t } = useTranslation()

  const { currentOrg, updateAvatar } = useCurrentOrgContext()
  const canEditOrg = useOrgPerm(OrgPermission.ProfileEdit)
  const dateFmtDefault = useDateFmt()

  const { imageCache } = useCacheContext()
  const cachedAvatar = !!currentOrg && !!imageCache.values[`org-${currentOrg.id}`]

  const [avatar, setAvatar] = useState<string | undefined>(currentOrg?.avatar || undefined)
  const [isAvatarLoading, setIsAvatarLoading] = useBoolean(false)

  const handleRemoveAvatar = () => {
    if (currentOrg) {
      setIsAvatarLoading(true)
      setAvatar(undefined)
      toasted('Removing avatar...')
      updateAvatar(undefined, {
        afterUpdate: () => setIsAvatarLoading(false),
        successMessage: t('Avatar removed!'),
      })
    }
  }

  const handleChangeAvatar = (nextResult: ImgEasyResult | null) => {
    if (nextResult && nextResult.file && currentOrg) {
      setIsAvatarLoading(true)
      setAvatar(nextResult.src)
      toasted('Uploading avatar...')
      updateAvatar(
        { src: nextResult.src, file: nextResult.file },
        {
          afterUpdate: () => {
            setIsAvatarLoading(false)
            imageCache.update(`org-${currentOrg.id}`, Date.now())
          },
          successMessage: t('Organization updated!'),
        }
      )
    }
  }

  return currentOrg ? (
    <div className='flex flex-col gap-2'>
      {canEditOrg ? (
        <UploadAvatar
          id='org-detail-avatar'
          canEdit={canEditOrg}
          avatar={avatar}
          name={currentOrg.name}
          size={AvatarSize.XL2}
          isLoading={isAvatarLoading}
          btnIconWrapperClassName='border-4 border-zinc-100 dark:border-slate-800'
          btnStyleBorder={false}
          onRemove={handleRemoveAvatar}
          onChange={handleChangeAvatar}
          withCache={cachedAvatar}
        />
      ) : (
        <FallbackAvatar size={AvatarSize.XL2} text={currentOrg.name} src={currentOrg.avatar} withCache={cachedAvatar} />
      )}
    </div>
  ) : null
}

export default WelcomeAvatar
